import { useContext, useEffect, useRef, useState } from 'react';
import ModalComponent from 'components/Common/Modal';
import useUploadFiles from 'api/hooks/useUploadFiles';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { UploadHubListItem } from 'api/services/uploadFileService';
import { store } from 'components/Store';
import UploadHubColourPicker from 'components/UploadHub/UploadHubModalAdd/UploadHubColourPicker';
import UploadHubLogoPicker from 'components/UploadHub/UploadHubModalAdd/UploadHubLogoPicker';
import useLogoUrlReplacement from 'components/Dashboard/UserContent/ui/useLogoUrlReplacement';
import { getLogoBackgroundColor } from 'components/UploadHub/UploadHubModalAdd/getLogoBackgroundColor';
import UploadHubFileField from 'components/UploadHub/UploadHubModalAdd/UploadHubFileField';
import UploadHubLandingPreview from 'components/UploadHub/UploadHubModalAdd/UploadHubLandingPreview';
import { BAR_BACKGROUND_DARKEN, DEFAULT_BACKGROUND_COLOUR, darkenHexColor } from 'utils/documentPreviewColors';
import { useSiteSectionsNames } from 'utils/getAppSite';

type Step = 'file' | 'landingPage';
type UploadHubModalMode = 'add' | 'editFile' | 'editDesign';

interface UploadHubModalAddProps {
  openState: {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
  };
  filesState?: {
    listFiles: Array<UploadHubListItem>;
    setListFiles: (value: Array<UploadHubListItem>) => void;
  };
  mode?: UploadHubModalMode;
  editingFile?: UploadHubListItem | null;
  onUpdated?: (file: UploadHubListItem) => void;
}

const UploadHubModalAdd = (props: UploadHubModalAddProps) => {
  const { openState, filesState, mode = 'add', editingFile, onUpdated } = props ?? {};
  const { listFiles, setListFiles } = filesState ?? {};
  const { isOpen, setIsOpen } = openState ?? {};
  const { addItemToUploadHub, updateItemFromUploadHub } = useUploadFiles();
  const { state } = useContext(store) as unknown as { state: { team?: { organisationLogo?: string } } };
  const { replacementOfUrl } = useLogoUrlReplacement();

  const lockedStep: Step | null = mode === 'editFile' ? 'file' : mode === 'editDesign' ? 'landingPage' : null;

  const initialFileName = editingFile?.fileName ?? '';
  const initialLogo = editingFile?.organisationLogoUrl ?? state.team?.organisationLogo ?? null;
  const initialColour = editingFile?.backgroundColor ?? DEFAULT_BACKGROUND_COLOUR;
  const initialBarColour = editingFile?.barBackgroundColor ?? darkenHexColor(initialColour, BAR_BACKGROUND_DARKEN);

  const [step, setStep] = useState<Step>(lockedStep ?? 'file');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileRemoved, setFileRemoved] = useState(false);
  const [fileName, setFileName] = useState(initialFileName);
  const [organisationLogo, setOrganisationLogo] = useState<string | null>(initialLogo);
  const [backgroundColour, setBackgroundColour] = useState(initialColour);
  const [barBackgroundColour, setBarBackgroundColour] = useState(initialBarColour);
  const [isUploading, setIsUploading] = useState(false);
  const currentStep = lockedStep ?? step;

  const hasFile = pendingFiles.length > 0 || (mode !== 'add' && !fileRemoved);
  const isFileStepDirty = pendingFiles.length > 0 || fileRemoved || fileName.trim() !== initialFileName.trim();
  const isDesignStepDirty =
    organisationLogo !== initialLogo || backgroundColour !== initialColour || barBackgroundColour !== initialBarColour;

  const inputElement = useRef<HTMLInputElement>(null);

  const changeBackgroundColour = (colour: string) => {
    setBackgroundColour(colour);
    setBarBackgroundColour(darkenHexColor(colour, BAR_BACKGROUND_DARKEN));
  };

  const changeLogo = (logo: string | null) => {
    setOrganisationLogo(logo);
    if (!logo) {
      changeBackgroundColour(DEFAULT_BACKGROUND_COLOUR);
      return;
    }
    getLogoBackgroundColor({ logoUrl: logo, replacementOfUrl }).then((color) =>
      changeBackgroundColour(color ?? DEFAULT_BACKGROUND_COLOUR)
    );
  };

  useEffect(() => {
    if (mode !== 'add' || !initialLogo) return;
    let cancelled = false;
    getLogoBackgroundColor({ logoUrl: initialLogo, replacementOfUrl }).then((color) => {
      if (!cancelled) changeBackgroundColour(color ?? DEFAULT_BACKGROUND_COLOUR);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (inputElement?.current) {
        inputElement.current?.focus();
      }
    }, 300);
  }, [isOpen]);

  const resetState = () => {
    setStep(lockedStep ?? 'file');
    setPendingFiles([]);
    setFileRemoved(false);
    setFileName(initialFileName);
    setOrganisationLogo(initialLogo);
    setBackgroundColour(initialColour);
    setBarBackgroundColour(initialBarColour);
  };

  const handleUpload = async () => {
    if (pendingFiles.length === 0 || fileName.trim() === '') return;
    setIsUploading(true);
    try {
      const createdItems: UploadHubListItem[] = await addItemToUploadHub({
        files: pendingFiles,
        fileName,
        organisationLogo,
        color: backgroundColour,
        barColor: barBackgroundColour,
      });
      createNotification('success', 'Files uploaded successfully');
      setListFiles?.([...createdItems, ...(listFiles ?? [])]);
      setIsOpen(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveEdits = async () => {
    if (!editingFile || fileName.trim() === '' || !hasFile) return;
    setIsUploading(true);
    try {
      const updated = await updateItemFromUploadHub({
        id: editingFile.id,
        file: pendingFiles[0],
        fileName,
        organisationLogo,
        color: backgroundColour,
        barColor: barBackgroundColour,
      });
      createNotification('success', 'File updated successfully');
      onUpdated?.({
        ...editingFile,
        fileName,
        organisationLogoUrl: organisationLogo ?? undefined,
        backgroundColor: backgroundColour,
        barBackgroundColor: barBackgroundColour,
        ...(updated ?? {}),
      });
      setIsOpen(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    } finally {
      setIsUploading(false);
    }
  };

  const showBackButton = !lockedStep && step === 'landingPage';

  const isSaveDisabled = () => {
    if (lockedStep === 'file') return fileName.trim() === '' || isUploading || !hasFile || !isFileStepDirty;
    if (lockedStep === 'landingPage') return isUploading || !isDesignStepDirty;
    if (step === 'file') return pendingFiles.length === 0 || fileName.trim() === '';
    return isUploading;
  };

  const { getName } = useSiteSectionsNames();

  return (
    <ModalComponent isOpen={isOpen} setIsOpen={setIsOpen} maxWidth={700} onAfterClose={resetState}>
      <div className='px-4 pb-3 specific-top-padding-20 border-bottom '>
        <h3 className={`title-h5-m-bold mb-1`}>
          {mode === 'editFile'
            ? 'Edit file'
            : currentStep === 'file'
              ? 'Upload a new file'
              : 'Create a landing page for your file'}
        </h3>
        {currentStep === 'landingPage' && (
          <p className='mb-0 main-light-text paragraph-p2'>
            This is how your file will be presented when shared with others
          </p>
        )}
      </div>
      <div className='p-4'>
        {currentStep === 'file' ? (
          <>
            <div className='mb-4'>
              <label className='font-weight-bold' htmlFor='uploadHubFileName'>
                File name
              </label>
              <input
                ref={inputElement}
                autoComplete='off'
                id='uploadHubFileName'
                className='main-input form-control'
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </div>

            <div className='pb-3'>
              <p className='mb-3 font-weight-bold'>File</p>
              <UploadHubFileField
                mode={mode}
                editingFile={editingFile}
                pendingFiles={pendingFiles}
                setPendingFiles={setPendingFiles}
                fileRemoved={fileRemoved}
                setFileRemoved={setFileRemoved}
                onFilesAdded={([file]) => {
                  if (fileName === '') setFileName(file.name);
                }}
              />
              {mode === 'editFile' && pendingFiles.length > 0 && (
                <div className='border rounded bg-blue-hovered p-3 d-flex main-subtle-text mt-3'>
                  <span
                    className='icon-info-circle-filled title-h2'
                    style={{ marginTop: '2px', marginRight: '12px' }}
                  />
                  <span className='pr-lg-5'>
                    Replacing this file will also change it in any emails and lobbying materials you have already sent
                  </span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className='d-flex align-items-start pb-3'>
            <div className='flex-grow-1 pr-5'>
              <div className='mb-4'>
                <p className='font-weight-bold mb-2'>Background {getName({ property: 'Colour' })?.toLowerCase()}</p>
                <UploadHubColourPicker color={backgroundColour} setColor={changeBackgroundColour} />
              </div>
              <div className='mb-4'>
                <p className='font-weight-bold mb-2'>Navigation bar {getName({ property: 'Colour' })?.toLowerCase()}</p>
                <UploadHubColourPicker color={barBackgroundColour} setColor={setBarBackgroundColour} />
              </div>
              <div className='border-top pt-3'>
                <label className='font-weight-bold'>Organisation logo</label>
                <UploadHubLogoPicker logo={organisationLogo} setLogo={changeLogo} />
              </div>
            </div>

            <div className='specific-top-margin-5' style={{ width: '220px', flexShrink: 0 }}>
              <UploadHubLandingPreview
                organisationLogo={organisationLogo}
                backgroundColour={backgroundColour}
                barBackgroundColour={barBackgroundColour}
              />
            </div>
          </div>
        )}
      </div>

      <div
        className={`rounded-bottom border-top px-4 d-flex justify-content-${showBackButton ? 'between' : 'end'} py-4 `}
      >
        {showBackButton && (
          <button className='general-button simple-link-button' onClick={() => setStep('file')}>
            <span className='paragraph-p6 icon-long-arrow-left d-inline-block-centered mr-2' />
            Back
          </button>
        )}
        <button
          className='general-button action-button rounded-pill px-4'
          disabled={isSaveDisabled()}
          onClick={() => {
            if (lockedStep) {
              handleSaveEdits();
            } else if (step === 'file') {
              setStep('landingPage');
            } else {
              handleUpload();
            }
          }}
        >
          {isUploading && <i className='fas fa-spinner fa-spin d-inline-block-centered mr-2' />}
          {lockedStep ? (
            'Save changes'
          ) : step === 'file' ? (
            <>
              Continue{' '}
              <span
                className='icon-long-arrow-right d-inline-block-centered centered-2 ml-2'
                style={{ fontSize: '9px' }}
              />
            </>
          ) : (
            'Upload file'
          )}
        </button>
      </div>
    </ModalComponent>
  );
};

export type { UploadHubModalMode };
export default UploadHubModalAdd;
